import { cn } from '@lfg/ui/lib';
import { Stack, StackProps, Typography } from '@mui/material';
import { TextareaHTMLAttributes } from 'react';
import { Control, Controller, FieldValues, Path, RegisterOptions } from 'react-hook-form';

import { gotham } from '@/assets/fonts';

interface TextareaProps<T extends FieldValues> {
  title?: string;
  stackProps?: StackProps;
  rows?: number;
  disableResize?: boolean;
  name: Path<T>;
  control: Control<T>;
  rules?: RegisterOptions<T>;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

export function Textarea<T extends FieldValues>({
  title = 'Title',
  className,
  style,
  stackProps,
  disableResize = false,
  rows = 3,
  name,
  control,
  rules,
  placeholder = 'Text',
  ...textareaProps
}: TextareaProps<T> & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  // Common styles shared between input and textarea
  const commonStyles = {
    fontFamily: gotham.style.fontFamily,
    ...style,
  };

  // Base classes shared between input and textarea
  const baseClassName =
    'w-full bg-[#FFFFFF05] border p-2 text-white placeholder-[#FFFFFF4D] focus:outline-none focus:ring-1 focus:ring-purple-500';

  return (
    <Stack width={'100%'} gap={'12px'} {...stackProps}>
      <Typography fontSize={{ xs: '14px', sm: '18px' }} variant={'bodyLarge'} color={'#FFFFFF'}>
        {title}
      </Typography>

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <textarea
              className={cn(
                baseClassName,
                'min-h-[80px] text-[14px] resize-y sm:text-[18px]',
                disableResize ? 'resize-none' : 'resize-y',
                error ? 'border-red-500' : 'border-[#FFFFFF0D]',
                className,
              )}
              placeholder={placeholder}
              rows={rows}
              style={commonStyles}
              {...field}
              {...textareaProps}
            />
            {error && (
              <Typography color='error' fontSize={{ xs: '12px', sm: '14px' }}>
                {error.message}
              </Typography>
            )}
          </>
        )}
      />
    </Stack>
  );
}
